'use client';

import { useEffect, useState } from 'react';
import { nb } from '@/lib/nobreak';

/**
 * The floating "unlock the system" card (owner, 2026-10-22: replace the floating demo card "with one that has a really great
 * CTA to unlock the system / try it free"). Appears once the hero leaves the viewport, like LPSticky, and hides over any
 * [data-lp-cta] block or the footer. A live pulse, the gradient ring, an eyebrow, the pitch, one body line and the lime key to
 * try.leadpops.com (data-try-link: the Start Here script adds the visitor's light or dark to the address as it is pressed).
 * All copy comes from the page's frontmatter (v2.sticky with variant: start).
 */
export function LPStartSticky({ href, label, pitch, eyebrow, body }: { href: string; label: string; pitch?: string; eyebrow?: string; body?: string }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const hero = document.getElementById('lp-hero');
    if (!hero) return;
    const watch = [...Array.from(document.querySelectorAll('[data-lp-cta]')), document.querySelector('footer')].filter(Boolean) as Element[];
    const seen = new Set<Element>();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? seen.add(e.target) : seen.delete(e.target)));
      setShow(!seen.has(hero) && !watch.some((w) => seen.has(w)));
    }, { threshold: 0 });
    io.observe(hero);
    watch.forEach((w) => io.observe(w));
    return () => io.disconnect();
  }, []);
  return (
    <div
      aria-hidden={!show}
      className={`lp-start-sticky fixed inset-x-3 bottom-3 z-40 transition-all duration-500 sm:inset-x-auto sm:left-1/2 sm:w-[min(44rem,calc(100vw-2rem))] sm:-translate-x-1/2 ${show ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-6 opacity-0'}`}
    >
      <div className="rounded-[26px] bg-gradient-to-br from-[#ff34a7] via-[#7c5cff] to-[#22e4ff] p-[1.5px] shadow-[0_30px_80px_-24px_rgba(0,0,0,.75)]">
        <div className="lp-start-sticky-in grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 rounded-[25px] bg-[#0B0C12]/95 p-4 text-white backdrop-blur sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:p-5">
          <span aria-hidden className="relative grid h-12 w-12 place-items-center rounded-[14px] bg-gradient-to-br from-[#b6ff5c] to-[#22e4ff] text-[#05060b]">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" /></svg>
            <span className="absolute -right-1 -top-1 flex h-3 w-3"><span className="lp-ping absolute inline-flex h-full w-full rounded-full bg-[#b6ff5c]" /><span className="relative inline-flex h-3 w-3 rounded-full bg-[#b6ff5c] ring-2 ring-[#0B0C12]" /></span>
          </span>
          <span className="min-w-0">
            {eyebrow && <small className="block text-[11px] font-bold uppercase tracking-[.16em] text-[#b6ff5c]">{nb(eyebrow)}</small>}
            {pitch && <b className="lp-display-sm mt-0.5 block text-[1.05rem] font-semibold leading-tight sm:text-[1.25rem]">{nb(pitch)}</b>}
            {body && <span className="mt-1 hidden text-[13.5px] leading-snug text-white/70 sm:block">{nb(body)}</span>}
          </span>
          <a href={href} data-try-link="" tabIndex={show ? 0 : -1} className="lp-shimmer col-span-2 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#ff34a7] px-6 text-[15px] font-semibold text-white sm:col-span-1 sm:h-[3.25rem] sm:px-7">
            {nb(label)}
            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" /></svg>
          </a>
        </div>
      </div>
    </div>
  );
}
